// CodingAI.jsx
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../../Context/AuthContext";
import { PermissionCodes, isSuperAdminUser, permissionHas, resolveUserPermissionCodes } from "../../Utils/Permissions";
import { streamMedicalCodes } from "../../Services/CodingAIService";
import { getAllOrganizations } from "../../Services/OrganizationService";
import SearchableDropdown from "../../Components/Subscription/SearchableDropdown";
import { submitAiFeedback } from "../../Services/AiFeedbackService";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import PageTitle from "../../Components/Common/PageTitle";
import ConfidenceBadge from "../../Components/Common/ConfidenceBadge";
import AiFeedbackActions from "../../Components/Common/AiFeedbackActions";
import AnalyticsChatWidget from "../../Components/Analytics/AnalyticsChatWidget";
import { endpoints } from "../../Utils/Constants";
import { MdAutoAwesome, MdHelpOutline, MdWarningAmber } from "react-icons/md";

const validationSchema = Yup.object({
    clinical_text: Yup.string().trim().required("Clinical text is required"),
});

// statuses.id values for internal users who work across organizations.
const USER_TYPE_BILLING_MEMBER = 78;
const USER_TYPE_CREDENTIALING_MEMBER = 79;

// "Cardiovascular Disease (207RC0000X)" for each organization-level taxonomy, primary first.
// When the organization has a custom taxonomy, only that one is used (mirrors the coding API).
function formatOrganizationSpecialties(taxonomies = []) {
    const orgTaxonomies = taxonomies.filter((t) => !t.provider_id && t.status_id !== 5 && (t.code || t.description));
    const custom = orgTaxonomies.find((t) => t.is_custom);
    return (custom ? [custom] : orgTaxonomies)
        .sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary))
        .map((t) => (t.description ? `${t.description}${t.code ? ` (${t.code})` : ""}` : t.code));
}

const THINKING_MESSAGES = [
    "Reading the clinical documentation...",
    "Identifying CPT and HCPCS codes...",
    "Checking modifiers and add-on codes...",
    "Cross-checking ICD-10 diagnosis criteria...",
    "Validating the supported E/M level...",
    "Applying payer-specific coding rules...",
    "Reviewing documentation for compliance...",
    "Finalizing suggested codes...",
];

// Friendly labels for the entity types returned by the PHI screen (local ONNX PHI model).
const PHI_ENTITY_LABELS = {
    PERSON: "Name",
    PHONE_NUMBER: "Phone Number",
    EMAIL_ADDRESS: "Email Address",
    US_SSN: "SSN",
    LOCATION: "Location",
    CREDIT_CARD: "Credit Card Number",
    US_BANK_NUMBER: "Bank Account Number",
    US_DRIVER_LICENSE: "Driver's License Number",
    US_PASSPORT: "Passport Number",
    IBAN_CODE: "IBAN",
    IP_ADDRESS: "IP Address",
    MEDICAL_LICENSE: "Medical License Number",
    MEDICAL_RECORD_NUMBER: "Medical Record Number",
};

function mergePhiFindings(findings) {
    const sorted = [...findings].sort((a, b) => a.start - b.start || b.end - a.end);
    const merged = [];
    for (const f of sorted) {
        const last = merged[merged.length - 1];
        if (last && f.start <= last.end) {
            last.end = Math.max(last.end, f.end);
            last.entityTypes.add(f.entity_type);
        } else {
            merged.push({ start: f.start, end: f.end, entityTypes: new Set([f.entity_type]) });
        }
    }
    return merged;
}

// Renders the submitted clinical text with the flagged identifier spans highlighted.
function renderPhiHighlightedText(text, findings) {
    const merged = mergePhiFindings(findings);
    const nodes = [];
    let cursor = 0;
    merged.forEach((range, idx) => {
        if (range.start > cursor) {
            nodes.push(text.slice(cursor, range.start));
        }
        const label = Array.from(range.entityTypes).map((t) => PHI_ENTITY_LABELS[t] || t).join(", ");
        nodes.push(
            <mark key={`phi-${idx}`} className="phi-mark" title={label}>
                {text.slice(range.start, range.end)}
            </mark>
        );
        cursor = range.end;
    });
    if (cursor < text.length) {
        nodes.push(text.slice(cursor));
    }
    return nodes;
}

// Coding Assistant: a separate AI that explains, justifies or challenges the generated codes.
const ASSISTANT_WELCOME_PROMPTS = [
    "Are these codes correct for this note?",
    "What documentation would support a higher E/M level?",
    "Should any modifiers be added or changed?",
];

const ASSISTANT_QUICK_QUESTIONS = [
    "Are these codes correct for this note?",
    "What could I document to support a higher E/M level?",
    "Should any modifiers be added or changed?",
    "Are any diagnoses missing or not specific enough?",
];

const ASSISTANT_THINKING_MESSAGES = [
    "Re-reading the clinical note...",
    "Checking the suggested codes against the documentation...",
    "Reviewing CPT and ICD-10-CM guidelines...",
    "Weighing the alternatives...",
    "Checking modifier and E/M rules...",
    "Putting the explanation together...",
];

function AskWhyButton({ onClick, label = "Ask why" }) {
    return (
        <button type="button" className="coding-ai-ask-btn" onClick={onClick}>
            <MdHelpOutline size={15} aria-hidden="true" />
            {label}
        </button>
    );
}

// Labels for the cpt_codes category field.
const CPT_CATEGORY_LABELS = {
    em: "E/M",
    procedure: "Procedure",
    add_on: "Add-on",
    modifier: "Modifier",
    place_of_service: "Place of service",
    quality_reporting: "Quality reporting",
};

const PAYER_LABELS = {
    medicare: "Medicare",
    medicaid: "Medicaid",
    commercial: "Commercial",
    not_documented: "Not documented in the note",
};

function CptCard({ item, onAsk }) {
    const categoryLabel = CPT_CATEGORY_LABELS[item.category];
    return (
        <div className="rounded-3 p-3 result-card h-100 d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-bold item-code">{item.code}</span>
                    {item.units > 1 && <span className="small item-desc">&times; {item.units}</span>}
                    {categoryLabel && (
                        <span className="badge rounded-pill bg-primary-subtle hc-text-primary fw-semibold">
                            {categoryLabel}
                        </span>
                    )}
                    {item.billable === false && (
                        <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis fw-semibold">
                            Not billable
                        </span>
                    )}
                </div>
                <ConfidenceBadge confidence={item.confidence} />
            </div>
            <div className="small item-desc mt-2">{item.description}</div>
            {item.billable === false && item.billing_note && (
                <div className="icd-problem mt-2">{item.billing_note}</div>
            )}
            {item.supported_level && (
                <div className="icd-problem mt-2">Supported level &mdash; {item.supported_level}</div>
            )}
            {item.reason && <div className="small item-desc mt-2">{item.reason}</div>}
            {onAsk && (
                <div className="mt-auto pt-3">
                    <AskWhyButton
                        onClick={() =>
                            onAsk(
                                item.category === "em"
                                    ? `Why was ${item.code} chosen as the E/M level instead of a higher or lower level?`
                                    : `Why was ${item.code}${categoryLabel ? ` (${categoryLabel})` : ""} suggested? Is a different code, modifier or unit count more appropriate?`
                            )
                        }
                    />
                </div>
            )}
        </div>
    );
}

function IcdCard({ item, isPrimary = false, onAsk }) {
    return (
        <div className={`rounded-3 p-3 result-card h-100 d-flex flex-column ${isPrimary ? "is-primary" : ""} ${item.retained_below_threshold ? "is-retained" : ""}`}>
            <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-bold item-code">{item.code}</span>
                    {isPrimary && (
                        <span className="badge rounded-pill bg-primary-subtle hc-text-primary fw-semibold">Primary</span>
                    )}
                </div>
                <ConfidenceBadge confidence={item.confidence} />
            </div>
            <div className="small item-desc mt-2">{item.description}</div>
            {item.problem && <div className="icd-problem mt-2">{item.problem}</div>}
            {item.evidence && <div className="small item-desc mt-2">{item.evidence}</div>}
            {/* Explains why a code under the confidence threshold is still listed:
                it is the only code its problem has, and dropping it would leave
                a documented diagnosis uncoded. */}
            {item.retained_below_threshold && (
                <div
                    className="icd-retained mt-2"
                    title="Below the confidence threshold, kept because it is the only code for this problem. Verify before use."
                >
                    Only code for this problem &mdash; verify
                </div>
            )}
            {onAsk && (
                <div className="mt-auto pt-3">
                    <AskWhyButton
                        onClick={() =>
                            onAsk(
                                `Why was ${item.code} coded${isPrimary ? " as the primary diagnosis" : ""}? Does the note support a more specific ICD-10-CM code?`
                            )
                        }
                    />
                </div>
            )}
        </div>
    );
}

function UpgradeCard({ item, onAsk }) {
    return (
        <div className="rounded-3 p-3 p-md-4 result-card h-100 upgrade-card">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fw-bold item-code">{item.current_code}</span>
                <span className="upgrade-arrow" aria-hidden="true">&rarr;</span>
                <span className="fw-bold item-code upgrade-target">{item.suggested_code}</span>
                <ConfidenceBadge confidence={item.confidence} />
            </div>

            {item.description && <div className="small item-desc mb-3">{item.description}</div>}

            {item.missing_requirement && (
                <div className="mb-3">
                    <div className="upgrade-label mb-1">Missing requirement</div>
                    <div className="small item-desc">{item.missing_requirement}</div>
                </div>
            )}

            {item.potential_service_or_activity && (
                <div className="mb-3">
                    <div className="upgrade-label mb-1">What could support a higher level</div>
                    <div className="small item-desc">{item.potential_service_or_activity}</div>
                </div>
            )}

            {item.reason_not_supported && (
                <div className="mb-0">
                    <div className="upgrade-label mb-1">Why the note doesn&rsquo;t support it yet</div>
                    <div className="small item-desc">{item.reason_not_supported}</div>
                </div>
            )}

            {onAsk && (
                <div className="pt-3">
                    <AskWhyButton
                        label="Ask how to support it"
                        onClick={() =>
                            onAsk(
                                `What exactly would the provider need to document to support ${item.suggested_code} instead of ${item.current_code}?`
                            )
                        }
                    />
                </div>
            )}
        </div>
    );
}

// An additional code that applies only under a fact the note does not state (applies_if), or with
// documentation the note lacks. Shown for the user to choose from, never as a billed code.
function PossibleCodeCard({ item, onAsk }) {
    return (
        <div className="rounded-3 p-3 result-card h-100 d-flex flex-column upgrade-card">
            <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-bold item-code">{item.code}</span>
                    <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis fw-semibold">
                        Needs documentation
                    </span>
                </div>
                <ConfidenceBadge confidence={item.confidence} />
            </div>
            {item.description && <div className="small item-desc mt-2">{item.description}</div>}

            {item.applies_if && (
                <div className="possible-applies mt-3">
                    <div className="upgrade-label mb-1">Applies if</div>
                    <div className="possible-applies-text">{item.applies_if}</div>
                </div>
            )}

            {item.evidence && (
                <div className="mt-3">
                    <div className="upgrade-label mb-1">What in the note points to it</div>
                    <div className="small item-desc">{item.evidence}</div>
                </div>
            )}

            {item.missing_documentation && (
                <div className="mt-3">
                    <div className="upgrade-label mb-1">What&rsquo;s missing to bill it</div>
                    <div className="small item-desc">{item.missing_documentation}</div>
                </div>
            )}

            {onAsk && (
                <div className="mt-auto pt-3">
                    <AskWhyButton
                        onClick={() =>
                            onAsk(`When would ${item.code} apply to this encounter? What would the provider need to document to bill it?`)
                        }
                    />
                </div>
            )}
        </div>
    );
}

export default function CodingAI() {
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );
    const canViewCodingAI = permissionHas(userPermissionCodes, PermissionCodes.CODING_AI_VIEW, decodedToken);
    // Super admins and internal users handle several organizations, so they pick the one whose
    // specialty should guide ICD-10 suggestions. External users are scoped by the API to their own.
    const canPickOrganization =
        isSuperAdminUser(decodedToken) ||
        [USER_TYPE_BILLING_MEMBER, USER_TYPE_CREDENTIALING_MEMBER].includes(Number(decodedToken?.user_type));

    const [organizations, setOrganizations] = React.useState([]);
    const [organizationsLoading, setOrganizationsLoading] = React.useState(false);
    const [selectedOrganizationId, setSelectedOrganizationId] = React.useState("");

    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [phiDetected, setPhiDetected] = React.useState(null);
    const [feedback, setFeedback] = React.useState(null);
    const [thinkingMessage, setThinkingMessage] = React.useState(THINKING_MESSAGES[0]);
    const [assistantLaunch, setAssistantLaunch] = React.useState(null);
    const textareaRef = React.useRef(null);
    // The note and organization the current result was generated from — the Coding Assistant
    // answers against these, not whatever is typed in the textarea afterwards.
    const submittedRef = React.useRef({ text: "", organizationId: undefined });
    // One coding session per page visit; a fresh chat_id per "Generate". Both are
    // sent with the request and echoed back in `data` for the feedback call.
    const sessionIdRef = React.useRef(uuidv4());
    const lastAdvanceRef = React.useRef(0);

    const advanceThinkingMessage = React.useCallback(() => {
        lastAdvanceRef.current = Date.now();
        setThinkingMessage((currentMessage) => {
            const currentIndex = THINKING_MESSAGES.indexOf(currentMessage);
            return THINKING_MESSAGES[(currentIndex + 1) % THINKING_MESSAGES.length];
        });
    }, []);

    React.useEffect(() => {
        if (!loading) return undefined;
        setThinkingMessage(THINKING_MESSAGES[0]);
        lastAdvanceRef.current = Date.now();
        const interval = setInterval(advanceThinkingMessage, 4000);
        return () => clearInterval(interval);
    }, [loading, advanceThinkingMessage]);

    React.useEffect(() => {
        if (!canPickOrganization || !canViewCodingAI || !token) return undefined;
        let cancelled = false;
        (async () => {
            setOrganizationsLoading(true);
            try {
                const response = await getAllOrganizations(token);
                const items = Array.isArray(response?.data?.Organizations) ? response.data.Organizations : [];
                if (cancelled) return;
                setOrganizations(
                    items
                        .filter((o) => o.id)
                        .map((o) => ({
                            value: o.id,
                            label: o.group_office_name || o.dba || o.cid || "Unnamed organization",
                            specialties: formatOrganizationSpecialties(o.taxonomies),
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
            } catch (err) {
                console.error(err);
                if (!cancelled) toast.error("Couldn't load organizations.");
            } finally {
                if (!cancelled) setOrganizationsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canPickOrganization, canViewCodingAI, token]);

    const selectedOrganization = organizations.find((o) => o.value === selectedOrganizationId) || null;

    const handleStreamProgress = React.useCallback(() => {
        if (Date.now() - lastAdvanceRef.current < 1500) return;
        advanceThinkingMessage();
    }, [advanceThinkingMessage]);

    const handleFeedback = async (sentiment) => {
        const sessionId = result?.session_id;
        const chatId = result?.chat_id;
        if (!sessionId || !chatId || feedback === sentiment) return;
        const previous = feedback;
        setFeedback(sentiment);
        const res = await submitAiFeedback(sessionId, chatId, sentiment, token);
        if (!res?.is_success) {
            setFeedback(previous);
            toast.error(res?.message || "Couldn't record that feedback.");
        }
    };

    const handleTextareaInput = (e) => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
        setPhiDetected(null);
        formik.handleChange(e);
    };

    const formik = useFormik({
        initialValues: { clinical_text: "" },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            setResult(null);
            setPhiDetected(null);
            setFeedback(null);
            setAssistantLaunch(null);
            submittedRef.current = {
                text: values.clinical_text,
                organizationId: canPickOrganization ? selectedOrganizationId : undefined,
            };
            try {
                await streamMedicalCodes(
                    values.clinical_text,
                    token,
                    {
                        sessionId: sessionIdRef.current,
                        chatId: uuidv4(),
                        organizationId: canPickOrganization ? selectedOrganizationId : undefined,
                    },
                    null,
                    (data) => setResult(data),
                    (phi) => {
                        const findings = phi?.phi_detected;
                        if (Array.isArray(findings) && findings.length > 0) {
                            setPhiDetected({ message: phi?.message, findings, text: values.clinical_text });
                        }
                    },
                    (message) => toast.error(message || "Failed to generate codes"),
                    handleStreamProgress
                );
            } catch (err) {
                console.error(err);
                toast.error(err.message || "Failed to generate codes");
            } finally {
                setLoading(false);
            }
        },
    });

    const handleReset = () => {
        formik.resetForm();
        setResult(null);
        setPhiDetected(null);
        setFeedback(null);
        setAssistantLaunch(null);
        setLoading(false);
        sessionIdRef.current = uuidv4(); // start a new coding session
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
    };

    React.useEffect(() => {
        if (textareaRef.current && formik.values.clinical_text !== undefined) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [formik.values.clinical_text]);

    if (!canViewCodingAI) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to access Coding AI.</div>
            </div>
        );
    }

    const cptCodes = Array.isArray(result?.cpt_codes)
        ? result.cpt_codes
        : result?.cpt
            ? [result.cpt]
            : [];
    const icd10CodesRaw = result?.icd10_codes || [];
    // The primary diagnosis is always shown; the high-confidence cut applies to secondaries only
    // (3 secondaries + the primary keeps the original "at least 4 codes" rule).
    const primaryIcd = icd10CodesRaw.find((item) => item.diagnosis_type === "primary") || null;
    const secondaryIcdRaw = icd10CodesRaw.filter((item) => item !== primaryIcd);
    const secondaryIcdFiltered = secondaryIcdRaw.filter(
        (item) => Number(item.confidence) >= 0.85
    );
    const secondaryIcd =
        secondaryIcdFiltered.length >= (primaryIcd ? 3 : 4) ? secondaryIcdFiltered : secondaryIcdRaw;
    const upgrade = result?.upgrade || (Array.isArray(result?.em_upgrades) ? result.em_upgrades[0] : null);
    const possibleCodes = Array.isArray(result?.possible_codes) ? result.possible_codes.filter((item) => item?.code) : [];
    const hasResults = cptCodes.length > 0 || !!primaryIcd || secondaryIcd.length > 0 || !!upgrade;
    const assistantReady = !loading && hasResults && !!result?.chat_id;
    const askAssistant = assistantReady ? (text) => setAssistantLaunch({ id: uuidv4(), text }) : undefined;

    return (
        <>
            <PageTitle title="Coding AI - Aegis 360" />

            {/* ── Input Card ── */}
            <div className="rounded-4 clean-card p-4 p-md-5 mb-4">
                <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-4">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h4 className="fw-bold mb-1" style={{ color: "var(--hc-heading-dark)" }}>Coding AI</h4>
                        <p className="text-muted small mb-0">
                            Suggests CPT/HCPCS and ICD-10 codes from clinical notes, tailored to the
                            organization&rsquo;s specialty.
                        </p>
                    </div>

                    {canPickOrganization && (
                        <div className="coding-ai-org-picker">
                            <SearchableDropdown
                                placeholder={organizationsLoading ? "Loading organizations..." : "All organizations (general)"}
                                options={organizations}
                                value={selectedOrganizationId}
                                onChange={(value) => setSelectedOrganizationId(value || "")}
                                disabled={organizationsLoading || loading}
                                loading={organizationsLoading}
                                emptyMessage="No organizations found"
                            />
                            {selectedOrganization && (
                                <div
                                    className="form-text mt-1"
                                    title={selectedOrganization.specialties.join(", ") || undefined}
                                >
                                    {selectedOrganization.specialties.length > 0
                                        ? `Specialty: ${selectedOrganization.specialties.join(", ")}`
                                        : "No specialty on file — general ICD-10 suggestions"}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-4">
                        <label className="form-label fw-semibold" style={{ color: "var(--hc-text-soft)" }}>
                            Clinical Text <span className="text-danger">*</span>
                        </label>
                        <textarea
                            ref={textareaRef}
                            className={`form-control rounded-3 ${formik.touched.clinical_text && formik.errors.clinical_text ? "is-invalid" : ""
                                }`}
                            rows={5}
                            style={{
                                resize: "none",
                                overflowY: "auto",
                                maxHeight: "400px",
                                backgroundColor: "var(--hc-tint-1)",
                                border: "1px solid var(--hc-line)",
                                padding: "1rem"
                            }}
                            placeholder="Paste clinical notes, diagnosis, or encounter documentation here..."
                            name="clinical_text"
                            value={formik.values.clinical_text}
                            onChange={handleTextareaInput}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.clinical_text && formik.errors.clinical_text && (
                            <div className="invalid-feedback d-block">{formik.errors.clinical_text}</div>
                        )}

                        {phiDetected && (
                            <div className="alert alert-danger rounded-3 mt-3 mb-0" role="alert">
                                <div className="d-flex align-items-start gap-2">
                                    <MdWarningAmber size={20} className="mt-1 flex-shrink-0" />
                                    <div className="flex-grow-1">
                                        <div className="fw-bold mb-1">Protected Health Information Detected</div>
                                        <div className="small mb-2">{phiDetected.message}</div>
                                        <div
                                            className="rounded-3 p-3 small"
                                            style={{ backgroundColor: "var(--hc-surface)", border: "1px solid #fca5a5", whiteSpace: "pre-wrap" }}
                                        >
                                            {renderPhiHighlightedText(phiDetected.text, phiDetected.findings)}
                                        </div>
                                        <div className="small text-muted mt-2">
                                            Flagged:{" "}
                                            {Array.from(
                                                new Set(phiDetected.findings.map((f) => PHI_ENTITY_LABELS[f.entity_type] || f.entity_type))
                                            ).join(", ")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="d-flex flex-column flex-lg-row align-items-center justify-content-end gap-3 mb-0">
                        <button
                            type="button"
                            className="btn btn-light px-4 fw-medium"
                            onClick={handleReset}
                            style={{ color: "var(--hc-text-soft)", border: "1px solid var(--hc-line)" }}
                        >
                            Reset
                        </button>

                        <button
                            type="submit"
                            className="btn hc-bg-primary text-white px-4 fw-medium"
                            disabled={loading || formik.isSubmitting}
                            style={{ boxShadow: "0 4px 12px rgba(13, 110, 253, 0.2)" }}
                        >
                            {loading || formik.isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2 text-white" role="status" aria-hidden="true" />
                                    Generating...
                                </>
                            ) : (
                                "Generate Codes"
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* ── Results Panel ── */}
            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary rounded-4">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-12">
                        <div className="mb-4">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
                                <span
                                    className="px-3 py-2 rounded-pill fw-semibold"
                                    style={{
                                        fontSize: "0.82rem",
                                        backgroundColor: "rgba(255,255,255,0.12)",
                                        color: "#fff",
                                    }}
                                >
                                    Coding AI Suggestions
                                </span>
                                {assistantReady && (
                                    <AnalyticsChatWidget
                                        // A new result is a new conversation.
                                        key={result.chat_id}
                                        requestContext={{
                                            clinical_text: submittedRef.current.text,
                                            coding_result: result,
                                            organization_id: submittedRef.current.organizationId || null,
                                        }}
                                        chatEndpoint={endpoints.CODING_AI_ASSISTANT_CHAT}
                                        summaryEndpoint={null}
                                        permissionCode={PermissionCodes.CODING_AI_VIEW}
                                        title="Coding Assistant"
                                        subtitle="Explains and double-checks these codes"
                                        triggerLabel="Ask Coding Assistant"
                                        emptyTitle="Questions about these codes?"
                                        emptyDescription="Ask why a code was chosen, what would support a different code, modifier or E/M level, or whether a suggestion is correct."
                                        welcomePrompts={ASSISTANT_WELCOME_PROMPTS}
                                        thinkingMessages={ASSISTANT_THINKING_MESSAGES}
                                        placeholder="Ask why a code was chosen, or what would change it..."
                                        launchRequest={assistantLaunch}
                                    />
                                )}
                            </div>
                        </div>

                        {!loading && !hasResults && (
                            <div className="text-center py-5">
                                <p className="mb-0 text-white-50" style={{ fontSize: "1.05rem" }}>
                                    No results yet. Enter clinical text above and click Generate Codes.
                                </p>
                            </div>
                        )}

                        {loading && !hasResults && (
                            <div className="py-5 d-flex flex-column align-items-center justify-content-center">
                                <div className="coding-ai-loader mb-3">
                                    <span /><span /><span />
                                </div>
                                <div className="fw-bold text-white" style={{ fontSize: "1.05rem" }}>
                                    Analyzing clinical text...
                                </div>
                                <div className="text-white-50 mt-1" style={{ fontSize: "0.9rem" }} aria-live="polite">
                                    {thinkingMessage}
                                </div>
                            </div>
                        )}

                        {hasResults && (
                            <div className="mt-2">
                                {loading && (
                                    <div className="d-flex align-items-center gap-2 mb-4 text-white-50 small" aria-live="polite">
                                        <span className="coding-ai-loader">
                                            <span /><span /><span />
                                        </span>
                                        {thinkingMessage}
                                    </div>
                                )}
                                <div className="row g-4">
                                    <div className="col-12">
                                        <h5 className="fw-bold section-title mb-3">CPT / HCPCS Codes</h5>
                                        {result?.payer && (
                                            <p className="text-white-50 small mb-3">
                                                Payer: {PAYER_LABELS[result.payer.payer_type] || result.payer.payer_type}
                                                {result.payer.note && <> &mdash; {result.payer.note}</>}
                                            </p>
                                        )}
                                        {cptCodes.length === 0 ? (
                                            <div className="rounded-3 bg-light p-3 border border-light text-muted">
                                                No CPT or HCPCS codes suggested.
                                            </div>
                                        ) : (
                                            <div className="row g-3">
                                                {cptCodes.map((item, idx) => (
                                                    <div key={`cpt-${idx}`} className="col-xl-6 col-12">
                                                        <CptCard item={item} onAsk={askAssistant} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {possibleCodes.length > 0 && (
                                        <div className="col-12 mt-5">
                                            <h5 className="fw-bold section-title mb-2">Possible Additional Codes</h5>
                                            <p className="text-white-50 small mb-3">
                                                Codes that could be billed alongside the codes above, depending on details the
                                                note doesn&rsquo;t state. Check the condition on each and choose the ones that match
                                                this encounter. These are queries for the treating provider &mdash; not instructions to bill.
                                            </p>
                                            <div className="row g-3">
                                                {possibleCodes.map((item, idx) => (
                                                    <div key={`possible-${item.code}-${idx}`} className="col-xl-6 col-12">
                                                        <PossibleCodeCard item={item} onAsk={askAssistant} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-12 mt-5">
                                        <h5 className="fw-bold section-title mb-3">ICD-10 Codes</h5>
                                        {!primaryIcd && secondaryIcd.length === 0 ? (
                                            <div className="rounded-3 bg-light p-3 border border-light text-muted">
                                                No ICD-10 codes suggested.
                                            </div>
                                        ) : (
                                            <>
                                                {primaryIcd && (
                                                    <>
                                                        <div className="icd-group-label mb-2">Primary Diagnosis</div>
                                                        <div className="row g-3">
                                                            <div className="col-xl-8 col-12">
                                                                <IcdCard item={primaryIcd} isPrimary onAsk={askAssistant} />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {secondaryIcd.length > 0 && (
                                                    <>
                                                        {primaryIcd && (
                                                            <div className="icd-group-label mt-4 mb-2">Secondary Diagnoses</div>
                                                        )}
                                                        <div className="row g-3">
                                                            {secondaryIcd.map((item, idx) => (
                                                                <div key={`icd10-${idx}`} className="col-lg-4 col-md-6 col-12">
                                                                    <IcdCard item={item} onAsk={askAssistant} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="col-12 mt-5">
                                        <h5 className="fw-bold section-title mb-2">E/M Level Upgrade</h5>

                                        <p className="text-white-50 small mb-3">
                                            What the documentation must contain to support the next E/M level up.
                                            These are queries for the treating provider &mdash; not instructions to bill.
                                        </p>

                                        {!upgrade ? (
                                            <div className="rounded-3 bg-light p-3 border border-light text-muted">
                                                No E/M level upgrade available for this encounter.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="row g-3">
                                                    <div className="col-xl-8 col-12">
                                                        <UpgradeCard item={upgrade} onAsk={askAssistant} />
                                                    </div>
                                                </div>

                                                {result?.disclaimer && (
                                                    <div className="upgrade-disclaimer small mt-3">
                                                        {result.disclaimer}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {assistantReady && (
                                        <div className="col-12 mt-5">
                                            <div className="coding-ai-assist-cta rounded-4 p-3 p-md-4">
                                                <div className="d-flex align-items-start gap-3 mb-3">
                                                    <span className="coding-ai-assist-icon" aria-hidden="true">
                                                        <MdAutoAwesome size={20} />
                                                    </span>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="fw-bold text-white">Not sure about a code?</div>
                                                        <div className="small text-white-50">
                                                            The Coding Assistant explains, justifies or challenges any suggestion
                                                            above, grounded in your clinical note.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {ASSISTANT_QUICK_QUESTIONS.map((question) => (
                                                        <button
                                                            key={question}
                                                            type="button"
                                                            className="coding-ai-assist-chip"
                                                            onClick={() => askAssistant(question)}
                                                        >
                                                            {question}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!loading && result?.chat_id && (
                                        <div className="col-12 mt-4">
                                            <AiFeedbackActions
                                                feedback={feedback}
                                                onFeedback={handleFeedback}
                                                size="md"
                                                variant="card"
                                                label="Were these suggestions helpful?"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
